/**
 * ProgressChart — Simple strength-score-over-time chart for a client.
 *
 * Uses a lightweight SVG-free approach: horizontal bars for each data point.
 * Reads the last 12 weekly snapshots from strength_history.
 *
 * Zero effect on scoring/matchmaking — read-only display (doctrine compliant).
 */

import { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ActivityIndicator } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import { supabase } from '@/lib/supabase';
import { TrendingUp } from 'lucide-react-native';

interface DataPoint {
  date: string;    // YYYY-MM-DD
  score: number;
}

interface Props {
  clientId: string;
}

export default function ProgressChart({ clientId }: Props) {
  const { theme } = useTheme();
  const c = theme.colors;
  const [points, setPoints] = useState<DataPoint[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, [clientId]);

  const loadHistory = async () => {
    setLoading(true);
    try {
      // Try strength_history table (weekly snapshots)
      const { data, error } = await supabase
        .from('strength_history')
        .select('snapshot_date, score')
        .eq('profile_id', clientId)
        .order('snapshot_date', { ascending: true })
        .limit(12);

      if (!error && data && data.length > 0) {
        setPoints(
          data.map((d: any) => ({
            date: d.snapshot_date,
            score: d.score ?? 0,
          })),
        );
      } else {
        // Fallback: use workout dates to approximate progress
        const { data: workouts } = await supabase
          .from('workouts')
          .select('performed_at, total_volume')
          .eq('user_id', clientId)
          .order('performed_at', { ascending: true })
          .limit(30);

        if (workouts && workouts.length > 0) {
          // Group by week
          const weekMap = new Map<string, number>();
          for (const w of workouts as any[]) {
            const d = new Date(w.performed_at);
            const weekKey = getWeekKey(d);
            weekMap.set(weekKey, (weekMap.get(weekKey) ?? 0) + (w.total_volume ?? 0));
          }
          const weekPoints = Array.from(weekMap.entries())
            .slice(-12)
            .map(([date, vol]) => ({ date, score: Math.round(vol / 100) }));
          setPoints(weekPoints);
        } else {
          setPoints([]);
        }
      }
    } catch (err) {
      console.warn('[ProgressChart] load error', err);
      setPoints([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <ActivityIndicator color={c.accent} style={{ marginVertical: 16 }} />;
  }

  if (points.length === 0) {
    return (
      <View style={[styles.container, { backgroundColor: c.surface, borderColor: c.border }]}>
        <View style={styles.headerRow}>
          <TrendingUp size={16} color={c.accent} />
          <Text style={[styles.title, { color: c.text, fontFamily: TYPO.heading.semibold }]}>
            PROGRESS
          </Text>
        </View>
        <Text style={[styles.emptyText, { color: c.textTertiary }]}>
          Not enough data to show a trend yet.
        </Text>
      </View>
    );
  }

  const minScore = Math.min(...points.map((p) => p.score));
  const maxScore = Math.max(...points.map((p) => p.score));
  const range = maxScore - minScore || 1;

  const firstScore = points[0].score;
  const lastScore = points[points.length - 1].score;
  const delta = lastScore - firstScore;
  const trendColor = delta >= 0 ? '#22c55e' : '#ef4444';

  return (
    <View style={[styles.container, { backgroundColor: c.surface, borderColor: c.border }]}>
      <View style={styles.headerRow}>
        <TrendingUp size={16} color={c.accent} />
        <Text style={[styles.title, { color: c.text, fontFamily: TYPO.heading.semibold }]}>
          PROGRESS
        </Text>
        <Text style={[styles.delta, { color: trendColor, fontFamily: TYPO.heading.bold }]}>
          {delta >= 0 ? '+' : ''}{delta}
        </Text>
      </View>

      {/* Sparkline-style bars */}
      <View style={styles.chartArea}>
        {points.map((pt, i) => {
          const height = 8 + ((pt.score - minScore) / range) * 52; // 8-60px
          return (
            <View key={`${pt.date}-${i}`} style={styles.barCol}>
              <View
                style={[
                  styles.bar,
                  {
                    height,
                    backgroundColor: i === points.length - 1 ? c.accent : c.accent + '66',
                  },
                ]}
              />
            </View>
          );
        })}
      </View>

      {/* X-axis labels */}
      <View style={styles.xAxis}>
        <Text style={[styles.xLabel, { color: c.textTertiary }]}>
          {formatShortDate(points[0].date)}
        </Text>
        <Text style={[styles.xLabel, { color: c.textTertiary }]}>
          {formatShortDate(points[points.length - 1].date)}
        </Text>
      </View>
    </View>
  );
}

function getWeekKey(d: Date): string {
  // Monday of the week
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diff));
  return `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;
}

function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
    marginBottom: 12,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  title: {
    flex: 1,
    fontSize: 13,
    letterSpacing: 1,
  },
  delta: {
    fontSize: 16,
  },
  chartArea: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    height: 64,
    gap: 3,
  },
  barCol: {
    flex: 1,
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  bar: {
    width: '80%',
    borderRadius: 2,
    minHeight: 4,
  },
  xAxis: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 6,
  },
  xLabel: {
    fontSize: 11,
    fontWeight: '600',
  },
  emptyText: {
    fontSize: 12,
    textAlign: 'center',
    paddingVertical: 12,
  },
});
